'use client';
import React, { useState, useRef } from 'react';
import { Mic, MicOff, Square } from 'lucide-react';

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
}

export default function VoiceInputButton({ onTranscript }: VoiceInputButtonProps) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<unknown>(null);

  const startListening = () => {
    setError(null);
    // Backend integration point: Replace with your preferred speech-to-text service
    // (e.g., Deepgram, AssemblyAI, or browser Web Speech API)
    const SpeechRecognition =
      (typeof window !== 'undefined' &&
        ((window as unknown as Record<string, unknown>).SpeechRecognition ||
          (window as unknown as Record<string, unknown>).webkitSpeechRecognition)) as
        | (new () => {
            continuous: boolean;
            interimResults: boolean;
            lang: string;
            start(): void;
            stop(): void;
            onresult: ((e: { results: { transcript: string }[][] }) => void) | null;
            onerror: ((e: { error: string }) => void) | null;
            onend: (() => void) | null;
          })
        | null;

    if (!SpeechRecognition) {
      setError('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      onTranscript(transcript);
      setListening(false);
    };

    recognition.onerror = (event) => {
      setError(`Voice input error: ${event.error}`);
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      (recognitionRef.current as { stop(): void }).stop();
    }
    setListening(false);
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={listening ? stopListening : startListening}
        className={[
          'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all duration-150 active:scale-95',
          listening
            ? 'bg-negative text-white border-negative animate-pulse' :'bg-card border-border text-secondary-foreground hover:bg-secondary hover:text-foreground',
        ].join(' ')}
        title={listening ? 'Stop recording — click to stop voice input' : 'Start voice input — speak item name and description'}
      >
        {listening ? (
          <>
            <Square size={15} className="fill-current" />
            Stop Recording
          </>
        ) : (
          <>
            <Mic size={15} />
            Speak Item
          </>
        )}
      </button>
      {listening && (
        <p className="text-xs text-negative animate-pulse flex items-center gap-1">
          <MicOff size={11} /> Listening… speak the item name and description
        </p>
      )}
      {error && (
        <p className="text-xs text-negative">{error}</p>
      )}
    </div>
  );
}